/**
 * Bark & Brush — AI Portrait Upload System
 * portrait-upload.js
 *
 * Flow: upload photo → AI generation → preview with live frame selector → add to cart
 */

(function () {
  'use strict';

  // ─── Configuration ──────────────────────────────────────────────────────────
  const API_URL = document.querySelector('.pus-section')?.dataset.apiUrl
    || 'https://YOUR-NETLIFY-SITE.netlify.app/api/generate-portrait';

  const MAX_IMAGE_DIMENSION = 1500; // Balanced: enough detail for AI, smaller payload = faster + fewer timeouts
  const JPEG_QUALITY = 0.85;

  // ─── State ──────────────────────────────────────────────────────────────────
  const state = {
    productHandle:             '',
    productTitle:              '',
    petName:                   '',
    photoId:                   null,
    originalBase64:            null,
    originalMimeType:          'image/jpeg',
    previewBase64:             null,
    previewMimeType:           'image/png',
    selectedSize:              null,
    selectedColour:            null,
    selectedVariant:           null,
    discountAmount:            0,   // detected discount in dollars
    shippingProtectionVariantId: null,
    shippingProtectionPrice:     0,    // in cents
    shippingProtectionAdded:     false,
    shippingProtectionLocked:    false, // true when carried over from a previous portrait
  };

  // Shopify variant data injected via Liquid
  const allVariants   = window.bbVariants   || [];
  const moneyFormat   = window.bbMoneyFormat || '${{amount}}';

  // ─── Discount Codes ──────────────────────────────────────────────────────────
  const DISCOUNT_MAP = {
    'WELCOME20': 20,
    'WELCOME15': 15,
    'LOYALTY30': 30,
    'RECALL25':  25,
    'TREAT25':   25,
    'TREAT20':   20,
  };

  // ─── Main Product Image ───────────────────────────────────────────────────────
  function findProductGallery() {
    // Try Dawn-specific selectors in order of specificity
    return (
      document.querySelector('media-gallery') ||
      document.querySelector('.product__media-gallery') ||
      document.querySelector('.product-media-gallery') ||
      document.querySelector('[id*="product-media"]') ||
      document.querySelector('.product__media-list')?.parentElement ||
      // Fallback: find the product image and walk up to a gallery-like wrapper
      (() => {
        const img = document.querySelector(
          '.product__media-item img, [data-media-type="image"] img, .product__media img'
        );
        if (!img) return null;
        let el = img.parentElement;
        for (let i = 0; i < 8; i++) {
          if (!el || el.tagName === 'SECTION' || el.tagName === 'FORM') break;
          const cls = (el.className || '') + (el.tagName || '');
          if (/media|gallery|slider/i.test(cls)) return el;
          el = el.parentElement;
        }
        return img.parentElement?.parentElement || null;
      })()
    );
  }

  function replaceMainImage(base64, mimeType) {
    const gallery = findProductGallery();
    console.log('[Bark & Brush] gallery found:', gallery);
    if (!gallery) return;

    // Hide the original gallery (no more CSS fighting)
    gallery.style.display = 'none';

    // Hide zoom button if present
    gallery.querySelectorAll('button').forEach(b => b.style.display = 'none');

    // Insert our portrait if not already there
    let portrait = document.getElementById('pus-main-portrait');
    if (!portrait) {
      portrait = document.createElement('div');
      portrait.id = 'pus-main-portrait';
      gallery.parentNode.insertBefore(portrait, gallery);
    }

    const nameOverlay = state.petName
      ? `<div style="position:absolute;top:13%;left:0;right:0;text-align:center;font-family:'Montserrat',sans-serif;font-weight:700;font-size:18px;letter-spacing:0.02em;color:#1a1a1a;text-transform:uppercase;pointer-events:none;z-index:2;">${state.petName.toUpperCase()}</div>`
      : '';

    portrait.innerHTML = `
      <div style="background:#f4f4f4;padding:28px 24px;text-align:center;">
        <div style="display:inline-block;max-width:min(400px,calc(100vw - 96px));">
          <div class="pus-main-frame frame-black" style="display:block;position:relative;">
            <img src="data:${mimeType};base64,${base64}"
                 style="display:block;width:100%;aspect-ratio:4/5;object-fit:cover;object-position:center center;">
            ${nameOverlay}
          </div>
        </div>
      </div>`;

    // Scroll to top of portrait
    portrait.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  function applyFrameToMainImage(frameClass) {
    const wrapper = document.querySelector('#pus-main-portrait .pus-main-frame');
    if (!wrapper) return;
    wrapper.classList.remove('frame-black', 'frame-white', 'frame-oak');
    if (frameClass) wrapper.classList.add(frameClass);
  }

  function restoreMainImage() {
    const gallery = findProductGallery();
    if (gallery) {
      gallery.style.display = '';
      gallery.querySelectorAll('button').forEach(b => b.style.display = '');
    }
    document.getElementById('pus-main-portrait')?.remove();
  }

  // ─── Discount Detection ───────────────────────────────────────────────────────
  async function detectDiscount() {
    // 1. Check URL param (?discount=CODE or ?discount_code=CODE)
    const params = new URLSearchParams(window.location.search);
    const urlCode = (params.get('discount') || params.get('discount_code') || '').toUpperCase();
    if (urlCode && DISCOUNT_MAP[urlCode] !== undefined) {
      return DISCOUNT_MAP[urlCode];
    }

    // 2. Check Shopify cart for applied discount codes
    try {
      const cart = await fetch('/cart.js').then(r => r.json());
      const codes = cart.discount_codes || [];
      for (const dc of codes) {
        const code = (dc.code || '').toUpperCase();
        if (DISCOUNT_MAP[code] !== undefined) return DISCOUNT_MAP[code];
      }
    } catch (_) {}

    return 0;
  }

  // ─── Init ────────────────────────────────────────────────────────────────────
  function init() {
    const section = document.querySelector('.pus-section');
    if (!section) return;

    state.productHandle = section.dataset.productHandle || '';
    state.productTitle  = section.dataset.productTitle  || '';

    setDeliveryDates();
    bindUploadEvents();
    bindVariantEvents();

    // Detect any active discount in the background
    detectDiscount().then(amount => { state.discountAmount = amount; });

    // Shipping protection toggle — only wired when the element is in the DOM
    if (document.getElementById('pus-sp-toggle')) {
      initShippingProtection();
    }
  }

  function setDeliveryDates() {
    const el = document.getElementById('pus-delivery-text');
    if (!el) return;
    const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    const now   = new Date();
    const start = new Date(now); start.setDate(start.getDate() + 3);
    const end   = new Date(now); end.setDate(end.getDate() + 6);
    const range = start.getMonth() === end.getMonth()
      ? `${MONTHS[start.getMonth()]} ${start.getDate()} – ${end.getDate()}`
      : `${MONTHS[start.getMonth()]} ${start.getDate()} – ${MONTHS[end.getMonth()]} ${end.getDate()}`;
    el.textContent = `Expected delivery ${range}`;
  }

  // ─── Upload Event Binding ────────────────────────────────────────────────────
  function isSignaturePortrait() {
    return state.productHandle === 'the-signature-portrait' || state.productHandle === 'the-classic';
  }

  function validateNameAndTrigger() {
    // Pet name is only required on the Signature Portrait product
    if (!isSignaturePortrait()) return true;
    const nameInput = document.getElementById('pus-pet-name');
    const name = nameInput?.value.trim() || '';
    if (!name) {
      nameInput?.classList.add('pus-input-error');
      nameInput?.focus();
      showError('Please enter your pet\'s name before uploading.');
      return false;
    }
    nameInput?.classList.remove('pus-input-error');
    state.petName = name;
    return true;
  }

  function bindUploadEvents() {
    const fileInput = document.getElementById('pus-file-input');
    fileInput?.addEventListener('change', (e) => {
      if (e.target.files[0]) handleFile(e.target.files[0]);
    });

    // Clear name error on input
    document.getElementById('pus-pet-name')?.addEventListener('input', () => {
      document.getElementById('pus-pet-name')?.classList.remove('pus-input-error');
      clearError();
    });

    document.getElementById('pus-browse-btn')?.addEventListener('click', (e) => {
      e.stopPropagation();
      if (!validateNameAndTrigger()) return;
      document.getElementById('pus-file-input')?.click();
    });

    const dropzone = document.getElementById('pus-dropzone');
    if (dropzone) {
      dropzone.addEventListener('click', () => {
        if (!validateNameAndTrigger()) return;
        document.getElementById('pus-file-input')?.click();
      });
      dropzone.addEventListener('dragover', (e) => {
        e.preventDefault();
        dropzone.classList.add('pus-drag-over');
      });
      dropzone.addEventListener('dragleave', () => {
        dropzone.classList.remove('pus-drag-over');
      });
      dropzone.addEventListener('drop', (e) => {
        e.preventDefault();
        dropzone.classList.remove('pus-drag-over');
        const file = e.dataTransfer?.files?.[0];
        if (file) {
          if (!validateNameAndTrigger()) return;
          handleFile(file);
        }
      });
    }

    document.getElementById('pus-retry-btn')?.addEventListener('click', () => {
      showTryAgainModal(
        // Use a new photo
        () => {
          clearError();
          document.getElementById('pus-file-input').value = '';
          resetVariants();
          restoreMainImage();
          const frame = document.getElementById('pus-preview-frame');
          if (frame) frame.style.display = '';
          showStep('upload');
        },
        // Regenerate with same photo
        null
      );
    });

    document.getElementById('pus-regenerate-btn')?.addEventListener('click', () => {
      if (!state.originalBase64) return;
      showTryAgainModal(
        // Use a new photo
        () => {
          clearError();
          document.getElementById('pus-file-input').value = '';
          resetVariants();
          restoreMainImage();
          const frame = document.getElementById('pus-preview-frame');
          if (frame) frame.style.display = '';
          showStep('upload');
        },
        // Regenerate with same photo
        async () => {
          clearError();
          restoreMainImage();
          const frame = document.getElementById('pus-preview-frame');
          if (frame) frame.style.display = '';
          showStep('generating');
          startProgressAnimation();
          resetVariants();
          try {
            const result = await callGenerateAPI(state.originalBase64, state.originalMimeType);
            if (result.warning) {
              stopProgressAnimation(() => {});
              showStep('preview');
              showQualityWarning(
                result.issues,
                async () => {
                  showStep('generating');
                  startProgressAnimation();
                  try {
                    const forcedResult = await callGenerateAPI(state.originalBase64, state.originalMimeType, true);
                    await applyGenerationResult(forcedResult);
                  } catch (err) {
                    stopProgressAnimation(() => {});
                    showStep('preview');
                    showError(err.message || 'Could not regenerate. Please try again.');
                  }
                },
                () => showStep('preview')
              );
              return;
            }
            await applyGenerationResult(result);
          } catch (err) {
            stopProgressAnimation(() => {});
            showStep('preview');
            showError(err.message || 'Could not regenerate. Please try again.');
          }
        }
      );
    });

    document.getElementById('pus-order-btn')?.addEventListener('click', addToCart);
  }

  // ─── Shipping Protection Toggle ───────────────────────────────────────────────
  function initShippingProtection() {
    // Fetch the Shipping Protection+ product to get its default variant ID,
    // then sync the toggle with whatever is already in the cart.
    fetch('/products/shipping-protection.js')
      .then(function (r) { return r.json(); })
      .then(function (p) {
        state.shippingProtectionVariantId = p.variants[0].id;
        state.shippingProtectionPrice     = p.variants[0].price; // cents
        // Sync toggle with current cart state
        return fetch('/cart.js').then(function (r) { return r.json(); });
      })
      .then(function (cart) {
        const alreadyInCart = cart.items.some(function (i) {
          return i.variant_id === state.shippingProtectionVariantId;
        });
        if (alreadyInCart) {
          state.shippingProtectionAdded  = true;
          state.shippingProtectionLocked = true; // carried from a previous portrait
          const toggle = document.getElementById('pus-sp-toggle');
          if (toggle) toggle.checked = true;
          updateBuyNowPrice();
        }
      })
      .catch(function (err) {
        console.warn('[BB] Could not load shipping protection product:', err);
      });

    document.getElementById('pus-sp-toggle')?.addEventListener('change', function (e) {
      if (!state.shippingProtectionVariantId) return;
      if (e.target.checked) {
        addShippingProtection();
      } else {
        removeShippingProtection();
      }
    });
  }

  function addShippingProtection() {
    const toggle = document.getElementById('pus-sp-toggle');
    if (toggle) toggle.disabled = true;

    // Guard: check cart first — never add a second shipping protection
    fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        const alreadyInCart = cart.items.some(function (i) {
          return i.variant_id === state.shippingProtectionVariantId;
        });
        if (alreadyInCart) {
          // Already there (added from a previous portrait) — just update state & price
          state.shippingProtectionAdded = true;
          updateBuyNowPrice();
          if (toggle) toggle.disabled = false;
          return;
        }
        doAddShippingProtection(toggle);
      })
      .catch(function () { doAddShippingProtection(toggle); });
  }

  function doAddShippingProtection(toggle) {
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: [{
          id: state.shippingProtectionVariantId,
          quantity: 1,
          properties: { '_Type': 'Shipping Protection' },
        }],
      }),
    })
    .then(function (r) {
      if (!r.ok) throw new Error('Could not add shipping protection.');
      state.shippingProtectionAdded = true;
      updateBuyNowPrice();
      return r.json();
    })
    .catch(function (err) {
      console.warn('[BB] Shipping protection add failed:', err);
      // Revert toggle on failure
      const t = document.getElementById('pus-sp-toggle');
      if (t) { t.checked = false; t.disabled = false; }
    })
    .finally(function () {
      const t = document.getElementById('pus-sp-toggle');
      if (t) t.disabled = false;
    });
  }

  function removeShippingProtection() {
    const toggle = document.getElementById('pus-sp-toggle');
    if (toggle) toggle.disabled = true;

    fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        const item = cart.items.find(function (i) {
          return i.variant_id === state.shippingProtectionVariantId;
        });
        if (!item) return;
        return fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: item.key, quantity: 0 }),
        }).then(function () {
          state.shippingProtectionAdded = false;
          updateBuyNowPrice();
        });
      })
      .catch(function (err) {
        console.warn('[BB] Shipping protection remove failed:', err);
        // Revert toggle on failure
        const t = document.getElementById('pus-sp-toggle');
        if (t) { t.checked = true; t.disabled = false; }
      })
      .finally(function () {
        const t = document.getElementById('pus-sp-toggle');
        if (t) t.disabled = false;
      });
  }

  function updateBuyNowPrice() {
    if (!state.selectedVariant) return;
    const btn = document.getElementById('pus-order-btn');
    if (!btn || btn.disabled) return;
    const discountCents = (state.discountAmount || 0) * 100;
    const base = Math.max(0, state.selectedVariant.price - discountCents);
    const total = state.shippingProtectionAdded
      ? base + state.shippingProtectionPrice
      : base;
    btn.textContent = 'Buy Now ' + formatMoney(total);
  }

  // ─── Digital File Only UI ─────────────────────────────────────────────────────
  function isDigitalFileOnly() {
    return state.selectedSize === 'Digital File Only';
  }

  function showDigitalFileUI(digital) {
    // Hide/show colour row
    const colourRow = document.getElementById('pus-colour-group');
    if (colourRow) colourRow.style.display = digital ? 'none' : '';

    // Hide/show frame preview
    const framePreview = document.getElementById('pus-preview-frame');
    if (framePreview && digital) framePreview.style.display = 'none';

    // Hide/show Shopify delivery estimate
    const deliveryEl = document.getElementById('pus-delivery');
    if (deliveryEl) deliveryEl.style.display = digital ? 'none' : '';

    // Hide/show shipping protection toggle (no shipping = no protection needed)
    const spWrap = document.getElementById('pus-sp-wrap');
    if (spWrap) {
      spWrap.style.display = digital ? 'none' : '';
      // If digital is selected and protection was added, remove it from cart
      if (digital && state.shippingProtectionAdded) {
        const toggle = document.getElementById('pus-sp-toggle');
        if (toggle) toggle.checked = false;
        removeShippingProtection();
      }
    }

    // Show/hide digital delivery message
    let msg = document.getElementById('pus-digital-msg');
    if (!msg) {
      msg = document.createElement('div');
      msg.id = 'pus-digital-msg';
      msg.style.cssText = 'background:#f5f0eb;border-radius:8px;padding:14px 16px;margin:12px 0;font-size:0.9rem;line-height:1.6;display:none;';
      msg.innerHTML = '📧 <strong>Digital delivery:</strong> Your high-resolution JPEG will be emailed to you within 24 hours of purchase.';
      const ticker = document.querySelector('.pus-trust-ticker');
      ticker?.parentNode?.insertBefore(msg, ticker);
    }
    msg.style.display = digital ? 'block' : 'none';
  }

  // ─── Variant Event Binding ────────────────────────────────────────────────────
  function bindVariantEvents() {
    // Size buttons
    document.querySelectorAll('.pus-size-btn').forEach((btn) => {
      btn.addEventListener('click', () => {
        document.querySelectorAll('.pus-size-btn').forEach(b => b.classList.remove('selected'));
        btn.classList.add('selected');
        state.selectedSize = btn.dataset.size;
        document.getElementById('pus-size-val').textContent = '— ' + btn.dataset.size;

        if (btn.dataset.size === 'Digital File Only') {
          showDigitalFileUI(true);
          // Auto-select first colour silently so variant matching works
          if (!state.selectedColour) {
            const firstColour = document.querySelector('.pus-colour-btn');
            if (firstColour) {
              state.selectedColour = firstColour.dataset.colour;
            }
          }
        } else {
          showDigitalFileUI(false);
        }

        onVariantChange();
      });
    });

    // Colour buttons
    document.querySelectorAll('.pus-colour-btn').forEach((btn) => {
      btn.addEventListener('click', () => {
        document.querySelectorAll('.pus-colour-btn').forEach(b => b.classList.remove('selected'));
        btn.classList.add('selected');
        state.selectedColour = btn.dataset.colour;
        document.getElementById('pus-colour-val').textContent = '— ' + btn.dataset.colour;
        updateFrameColour(btn.dataset.frameClass);
        onVariantChange();
      });
    });
  }

  // ─── Variant Logic ────────────────────────────────────────────────────────────
  function onVariantChange() {
    // Remove shipping protection only when the customer actively changes an
    // already-confirmed variant. Skip if locked (carried over from a previous portrait).
    if (state.shippingProtectionAdded && state.selectedVariant && !state.shippingProtectionLocked) {
      const toggle = document.getElementById('pus-sp-toggle');
      if (toggle) toggle.checked = false;
      removeShippingProtection();
    }

    if (!state.selectedSize || !state.selectedColour) {
      updateOrderButton(null);
      return;
    }

    // Flexible match — case-insensitive, trimmed, checks both option orderings
    const norm  = (s) => (s || '').trim().toLowerCase();
    const size   = norm(state.selectedSize);
    const colour = norm(state.selectedColour);

    const match = allVariants.find((v) => {
      const o1 = norm(v.option1);
      const o2 = norm(v.option2);
      return (o1 === size && o2 === colour) || (o1 === colour && o2 === size);
    });

    state.selectedVariant = match || null;
    console.log('[BB] Looking for:', size, '/', colour, '→', match ? 'FOUND $' + match.price : 'NONE');
    updateOrderButton(match || null);
  }

  function updateOrderButton(variant) {
    const btn       = document.getElementById('pus-order-btn');
    const priceEl   = document.getElementById('pus-price');
    const hint = document.getElementById('pus-variant-hint');

    if (!variant) {
      btn.disabled = true;
      priceEl?.classList.add('pus-hidden');
      hint?.classList.add('pus-visible');
      return;
    }

    hint?.classList.remove('pus-visible');
    priceEl?.classList.remove('pus-hidden');

    // Apply any active discount
    const discountCents = (state.discountAmount || 0) * 100;
    const displayPrice  = Math.max(0, variant.price - discountCents);

    if (priceEl) priceEl.textContent = formatMoney(displayPrice);

    // Price display with compare_at_price strikethrough
    const priceDisplay = document.getElementById('pus-price-display');
    const priceWas     = document.getElementById('pus-price-was');
    const priceNow     = document.getElementById('pus-price-now');
    if (priceDisplay && priceNow) {
      priceNow.textContent = formatMoney(displayPrice);
      // RRP = compare_at price if set, otherwise show original variant price when discount is active
      const rrp = variant.compare_at_price && variant.compare_at_price > variant.price
        ? variant.compare_at_price
        : (discountCents > 0 ? variant.price : null);
      if (priceWas) priceWas.textContent = rrp ? formatMoney(rrp) : '';
      // "You save" badge
      const priceSave = document.getElementById('pus-price-save');
      if (priceSave) {
        if (rrp && rrp > displayPrice) {
          priceSave.textContent = 'You save ' + formatMoney(rrp - displayPrice);
          priceSave.classList.remove('pus-hidden');
        } else {
          priceSave.classList.add('pus-hidden');
        }
      }
      priceDisplay.classList.remove('pus-hidden');
    }

    const available = variant.available !== false;
    btn.disabled = !available;
    btn.textContent = available
      ? 'Buy Now ' + formatMoney(displayPrice)
      : 'Unavailable';

    // ── Afterpay widget ──────────────────────────────────────────────────────
    const afterpayWrap = document.getElementById('pus-afterpay-wrap');
    const afterpayEl   = document.getElementById('pus-afterpay-placement');
    if (afterpayWrap && afterpayEl) {
      const dollars = (displayPrice / 100).toFixed(2);
      afterpayEl.setAttribute('data-amount', dollars);
      afterpayWrap.classList.remove('pus-hidden');
    }

    // ── Savings message ──────────────────────────────────────────────────────
    const savingsMsg = document.getElementById('pus-savings-msg');
    if (savingsMsg) {
      const rrpCents = variant.compare_at_price && variant.compare_at_price > variant.price
        ? variant.compare_at_price : variant.price;
      const totalSaving = Math.round((rrpCents - displayPrice) / 100);
      if (totalSaving > 0) {
        savingsMsg.textContent = discountCents > 0
          ? `You're saving $${totalSaving} today (discount applied) 🎉`
          : `You're saving $${totalSaving} today 🎉`;
        savingsMsg.classList.remove('pus-hidden');
      } else {
        savingsMsg.classList.add('pus-hidden');
      }
    }
  }

  function updateFrameColour(frameClass) {
    // Update the hidden bottom preview frame
    const frame = document.getElementById('pus-preview-frame');
    if (frame) {
      frame.classList.remove('frame-black', 'frame-white', 'frame-oak');
      if (frameClass) frame.classList.add(frameClass);
    }
    // Update the frame on the main product image at the top
    applyFrameToMainImage(frameClass);
  }

  function selectDefaultVariants() {
    // Click the first size and first colour button (whatever exists in Shopify)
    document.querySelector('.pus-size-btn')?.click();
    document.querySelector('.pus-colour-btn')?.click();
  }

  function resetVariants() {
    state.selectedSize    = null;
    state.selectedColour  = null;
    state.selectedVariant = null;

    document.querySelectorAll('.pus-size-btn, .pus-colour-btn').forEach(b => b.classList.remove('selected'));
    document.getElementById('pus-size-val').textContent   = '';
    document.getElementById('pus-colour-val').textContent = '';
    document.getElementById('pus-price')?.classList.add('pus-hidden');
    document.getElementById('pus-variant-hint')?.classList.remove('pus-visible');
    document.getElementById('pus-afterpay-wrap')?.classList.add('pus-hidden');
    document.getElementById('pus-savings-msg')?.classList.add('pus-hidden');

    const btn = document.getElementById('pus-order-btn');
    btn.disabled    = true;
    btn.textContent = 'Buy Now';

    updateFrameColour('frame-black'); // reset to default
  }

  // ─── File Handling ───────────────────────────────────────────────────────────
  async function applyGenerationResult(result) {
    state.photoId         = result.photoId;
    state.previewBase64   = result.previewBase64;
    state.previewMimeType = result.previewMimeType || 'image/png';

    const previewImg = document.getElementById('pus-preview-img');
    if (previewImg) previewImg.src = `data:${state.previewMimeType};base64,${state.previewBase64}`;

    replaceMainImage(state.previewBase64, state.previewMimeType);

    const frame = document.getElementById('pus-preview-frame');
    if (frame) frame.style.display = 'none';

    stopProgressAnimation(() => {
      showStep('preview');
      selectDefaultVariants();
    });
  }

  async function handleFile(file) {
    clearError();

    // Hard guard — name must be set (Signature Portrait only)
    if (isSignaturePortrait() && !state.petName) {
      const name = document.getElementById('pus-pet-name')?.value.trim() || '';
      if (!name) {
        document.getElementById('pus-pet-name')?.classList.add('pus-input-error');
        document.getElementById('pus-pet-name')?.focus();
        showError('Please enter your pet\'s name before uploading.');
        return;
      }
      state.petName = name;
    }

    if (!file.type.startsWith('image/')) {
      showError('Please upload an image file (JPG or PNG).');
      return;
    }
    if (file.size > 20 * 1024 * 1024) {
      showError('Please upload a photo smaller than 20MB.');
      return;
    }

    showStep('generating');
    startProgressAnimation();
    resetVariants();

    try {
      const { base64, mimeType } = await compressImage(file);
      state.originalBase64   = base64;
      state.originalMimeType = mimeType;
      const result = await callGenerateAPI(base64, mimeType);

      // Quality warning — pause, show modal, let user decide
      if (result.warning) {
        stopProgressAnimation(() => {});
        showStep('upload');
        showQualityWarning(
          result.issues,
          // Continue Anyway
          async () => {
            clearError();
            showStep('generating');
            startProgressAnimation();
            resetVariants();
            try {
              const forcedResult = await callGenerateAPI(base64, mimeType, true);
              await applyGenerationResult(forcedResult);
            } catch (err) {
              stopProgressAnimation(() => {});
              showStep('upload');
              showError(err.message || 'Something went wrong. Please try again.');
              console.error('[Bark & Brush AI]', err);
            }
          },
          // Choose Different Photo
          () => {
            clearError();
            showStep('upload');
          }
        );
        return;
      }

      await applyGenerationResult(result);

    } catch (err) {
      stopProgressAnimation(() => {});
      showStep('upload');
      showError(err.message || 'Something went wrong. Please try again with a different photo.');
      console.error('[Bark & Brush AI]', err);
    }
  }

  // ─── Image Compression ────────────────────────────────────────────────────────
  function compressImage(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => reject(new Error('Could not read image file.'));
      reader.onload = (e) => {
        const img = new Image();
        img.onerror = () => reject(new Error('Could not load image.'));
        img.onload = () => {
          let { width, height } = img;
          if (width > MAX_IMAGE_DIMENSION || height > MAX_IMAGE_DIMENSION) {
            if (width >= height) {
              height = Math.round((height / width) * MAX_IMAGE_DIMENSION);
              width  = MAX_IMAGE_DIMENSION;
            } else {
              width  = Math.round((width / height) * MAX_IMAGE_DIMENSION);
              height = MAX_IMAGE_DIMENSION;
            }
          }
          const canvas = document.createElement('canvas');
          canvas.width  = width;
          canvas.height = height;
          canvas.getContext('2d').drawImage(img, 0, 0, width, height);
          const dataUrl = canvas.toDataURL('image/jpeg', JPEG_QUALITY);
          resolve({ base64: dataUrl.split(',')[1], mimeType: 'image/jpeg' });
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    });
  }

  // ─── API Call ─────────────────────────────────────────────────────────────────
  async function callGenerateAPI(base64, mimeType, forceGenerate = false) {
    let response;
    try {
      response = await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          imageBase64:  base64,
          mimeType,
          styleKey:     state.productHandle,
          productTitle: state.productTitle,
          petName:      state.petName,
          forceGenerate,
        }),
      });
    } catch {
      throw new Error('Could not reach the portrait server. Please check your connection and try again 🐾');
    }

    const data = await response.json();
    // Quality warning — not an error, return for caller to handle
    if (data.warning) return data;
    if (!response.ok || data.error) throw new Error(data.error || `Server error (${response.status})`);
    return data;
  }

  // ─── Try Again Confirmation Modal ─────────────────────────────────────────────
  // onNewPhoto: always shown — upload a different photo
  // onRegenerate: shown only when triggered from Retry Design (null = hide that option)
  function showTryAgainModal(onNewPhoto, onRegenerate) {
    document.getElementById('pus-try-again-modal')?.remove();

    const modal = document.createElement('div');
    modal.id = 'pus-try-again-modal';
    modal.style.cssText = `
      position:fixed;inset:0;z-index:9999;
      background:rgba(0,0,0,0.55);
      display:flex;align-items:center;justify-content:center;
      padding:20px;
    `;

    modal.innerHTML = `
      <div style="
        background:#fff;border-radius:12px;padding:36px 28px;
        max-width:380px;width:100%;text-align:center;
        box-shadow:0 24px 64px rgba(0,0,0,0.22);
      ">
        <div style="font-size:3rem;line-height:1;margin-bottom:16px;">⚠️</div>
        <h3 style="font-size:1.625rem;font-weight:700;color:#1f1c26;margin:0 0 12px;">Not quite right?</h3>
        <p style="font-size:1.175rem;color:#666;margin:0 0 28px;line-height:1.55;">
          You'll <span style="color:#e03e2d;font-weight:600;">lose your current design</span> if you continue.<br>Are you sure you want to try again?
        </p>

        ${onRegenerate ? `
        <button id="pus-tam-regenerate" style="
          display:block;width:100%;padding:17px;margin-bottom:12px;
          background:#1f1c26;color:#fff;border:none;border-radius:8px;
          font-size:1.125rem;font-weight:600;cursor:pointer;letter-spacing:0.04em;
        ">Delete my current design and try again</button>
        ` : ''}

        <button id="pus-tam-newphoto" style="
          display:block;width:100%;padding:17px;margin-bottom:22px;
          background:#fff;color:#1f1c26;
          border:1.5px solid #1f1c26;border-radius:8px;
          font-size:1.125rem;font-weight:600;cursor:pointer;letter-spacing:0.04em;
        ">← Use a different photo</button>

        <button id="pus-tam-cancel" style="
          background:none;border:none;color:#1f1c26;
          font-size:1.25rem;font-weight:600;cursor:pointer;text-decoration:underline;text-underline-offset:3px;
        ">Cancel — keep my design</button>
      </div>
    `;

    document.body.appendChild(modal);

    const close = () => modal.remove();

    document.getElementById('pus-tam-cancel')?.addEventListener('click', close);
    modal.addEventListener('click', (e) => { if (e.target === modal) close(); });

    document.getElementById('pus-tam-regenerate')?.addEventListener('click', () => {
      close();
      onRegenerate?.();
    });

    document.getElementById('pus-tam-newphoto')?.addEventListener('click', () => {
      close();
      onNewPhoto?.();
    });
  }

  // ─── Quality Warning Modal ────────────────────────────────────────────────────
  function showQualityWarning(issues, onContinue, onRetry) {
    document.getElementById('pus-quality-modal')?.remove();

    const ISSUE_LABELS = {
      DARK:      { icon: '☀️', title: 'Image is Dark',       desc: 'A well-lit photo produces much better portrait results. Consider using a brighter photo.' },
      BLURRY:    { icon: '📷', title: 'Image is Blurry',     desc: 'Sharper photos make better portraits.' },
      TOO_SMALL: { icon: '🔍', title: 'Pet is Too Far Away', desc: 'Try a closer photo where your pet fills more of the frame.' },
    };

    const issueHTML = (issues || []).map(code => {
      const label = ISSUE_LABELS[code] || { icon: '⚠️', title: code, desc: '' };
      return `
        <div style="display:flex;align-items:flex-start;gap:12px;margin-bottom:14px;text-align:left;">
          <span style="font-size:1.3em;line-height:1;margin-top:1px;">${label.icon}</span>
          <div>
            <div style="font-weight:600;font-size:0.95em;color:#111;">${label.title}</div>
            ${label.desc ? `<div style="color:#666;font-size:0.85em;margin-top:3px;line-height:1.4;">${label.desc}</div>` : ''}
          </div>
        </div>`;
    }).join('');

    const modal = document.createElement('div');
    modal.id = 'pus-quality-modal';
    modal.innerHTML = `
      <div style="position:fixed;inset:0;background:rgba(0,0,0,0.55);z-index:99999;display:flex;align-items:center;justify-content:center;padding:20px;">
        <div style="background:#fff;border-radius:20px;padding:28px 24px;max-width:380px;width:100%;text-align:center;box-shadow:0 24px 64px rgba(0,0,0,0.25);">
          <div style="display:inline-flex;align-items:center;gap:6px;background:#fff8e6;border-radius:20px;padding:5px 14px;margin-bottom:16px;font-size:0.82em;font-weight:600;color:#b45309;">
            ⚠️ Heads up
          </div>
          <h3 style="margin:0 0 8px;font-size:1.1em;font-weight:700;color:#111;">This photo may not turn out great</h3>
          <p style="color:#777;font-size:0.88em;margin:0 0 20px;">A few things could affect your portrait:</p>
          <div style="margin-bottom:20px;">${issueHTML}</div>
          <button id="pus-quality-continue" style="display:block;width:100%;padding:14px;background:#fff;border:2px solid #111;border-radius:10px;font-size:1em;font-weight:600;cursor:pointer;margin-bottom:12px;color:#111;">Continue Anyway</button>
          <button id="pus-quality-retry" style="display:block;width:100%;padding:10px;background:none;border:none;font-size:0.92em;text-decoration:underline;cursor:pointer;color:#555;">Choose Different Photo</button>
        </div>
      </div>`;

    document.body.appendChild(modal);

    document.getElementById('pus-quality-continue').addEventListener('click', () => {
      modal.remove();
      if (onContinue) onContinue();
    });

    document.getElementById('pus-quality-retry').addEventListener('click', () => {
      modal.remove();
      document.getElementById('pus-file-input').value = '';
      if (onRetry) onRetry();
    });
  }

  // ─── Compress portrait to small thumbnail for cart preview ───────────────────
  function compressForCartPreview(base64, mimeType) {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = function () {
        const MAX = 150;
        const scale = Math.min(MAX / img.width, MAX / img.height, 1);
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const canvas = document.createElement('canvas');
        canvas.width  = w;
        canvas.height = h;
        canvas.getContext('2d').drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL('image/jpeg', 0.55));
      };
      img.onerror = () => resolve(null);
      img.src = 'data:' + mimeType + ';base64,' + base64;
    });
  }

  // ─── Add to Cart ─────────────────────────────────────────────────────────────
  async function addToCart() {
    if (!state.selectedVariant) {
      document.getElementById('pus-variant-hint')?.classList.add('pus-visible');
      return;
    }

    const btn = document.getElementById('pus-order-btn');
    btn.disabled    = true;
    btn.textContent = 'Processing…';

    try {
      const previewDataUrl = await compressForCartPreview(state.previewBase64, state.previewMimeType);

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{
            id:       state.selectedVariant.id,
            quantity: 1,
            properties: {
              '_Photo Reference ID':  state.photoId || 'N/A',
              'Pet Name':             state.petName || '',
              '_AI Preview Approved': 'Yes',
              'Portrait Style':       state.productTitle,
              '_Frame Size':          state.selectedSize,
              '_Frame Colour':        isDigitalFileOnly() ? 'N/A' : state.selectedColour,
              '_Order Type':          isDigitalFileOnly() ? 'Digital File' : 'Print',
              '_Portrait Preview':    previewDataUrl || '',
            },
          }],
        }),
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.description || 'Could not add to cart.');
      }

      // Show success panel (skip for digital file orders)
      if (isDigitalFileOnly()) {
        window.location.href = '/checkout';
      } else {
        showAddedToCartPanel();
      }

    } catch (err) {
      showError(err.message || 'Could not add to cart. Please try again.');
      btn.disabled    = false;
      btn.textContent = 'Buy Now — ' + formatMoney(state.selectedVariant.price);
    }
  }

  // ─── Added to Cart Panel ──────────────────────────────────────────────────────
  function showAddedToCartPanel() {
    const existing = document.getElementById('pus-added-panel');
    if (existing) existing.remove();

    // Check cart to see if they already have 2+ portraits (for auto-discount)
    fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        // Only count actual print portraits — exclude shipping protection and digital items
        const portraitCount = cart.items.filter(function (i) {
          return i.properties && i.properties['_Order Type'] === 'Print';
        }).length;

        const hasDouble = portraitCount >= 2;
        const checkoutUrl = hasDouble ? '/checkout?discount=DOUBLE50' : '/checkout';

        const panel = document.createElement('div');
        panel.id = 'pus-added-panel';
        panel.style.cssText = `
          position: fixed; inset: 0; z-index: 9999;
          background: rgba(0,0,0,0.55); display: flex;
          align-items: center; justify-content: center; padding: 20px;
        `;

        panel.innerHTML = hasDouble ? `
          <div style="background:#fff; border-radius:12px; padding:36px 28px;
                      max-width:420px; width:100%; text-align:center;
                      box-shadow:0 8px 40px rgba(0,0,0,0.18);">
            <h3 style="margin:0 0 10px; font-size:1.75rem; color:#1f1c26;">Portrait Added 💫</h3>
            <p style="margin:0 0 10px; color:#444; font-size:1.3rem; line-height:1.5;">
              You're getting <strong style="color:#2d6a4f;">$50 off your order!</strong>
            </p>
            <p style="margin:0 0 24px; font-size:1.2rem; color:#666;">
              Discount <strong style="background:#f5f0eb; padding:4px 12px; border-radius:4px; color:#1f1c26; letter-spacing:0.06em; font-size:1.25rem;">DOUBLE50</strong> applied automatically.
            </p>
            <a href="${checkoutUrl}"
               style="display:block; background:#1f1c26; color:#fff; text-decoration:none;
                      padding:16px; border-radius:8px; font-weight:600; font-size:1.2rem; margin-bottom:12px;">
              Continue to Checkout
            </a>
            <button id="pus-add-another-btn"
               style="display:block; width:100%; background:#f5f0eb; color:#1f1c26;
                      border:none; padding:18px; border-radius:8px; font-weight:600;
                      cursor:pointer; font-size:1.2rem;">
              Add Another Portrait 🐾
            </button>
          </div>
        ` : `
          <div style="background:#fff; border-radius:12px; padding:36px 28px;
                      max-width:420px; width:100%; text-align:center;
                      box-shadow:0 8px 40px rgba(0,0,0,0.18);">
            <h3 style="margin:0 0 10px; font-size:1.75rem; color:#1f1c26;">Portrait Added 💫</h3>
            <p style="margin:0 0 24px; color:#444; font-size:1.3rem; line-height:1.5;">
              Add another portrait and <strong style="color:#2d6a4f;">save $50 on your&nbsp;order!</strong>
            </p>
            <a href="${checkoutUrl}"
               style="display:block; background:#1f1c26; color:#fff; text-decoration:none;
                      padding:16px; border-radius:8px; font-weight:600; font-size:1.2rem; margin-bottom:12px;">
              Continue to Checkout
            </a>
            <button id="pus-add-another-btn"
               style="display:block; width:100%; background:#f5f0eb; color:#1f1c26;
                      border:none; padding:18px; border-radius:8px; font-weight:600;
                      cursor:pointer; font-size:1.2rem;">
              Add Another Portrait (Save $50) 🐾
            </button>
          </div>
        `;

        document.body.appendChild(panel);

        document.getElementById('pus-add-another-btn').addEventListener('click', function () {
          window.location.href = '/';
        });

        panel.addEventListener('click', function (e) {
          if (e.target === panel) {
            panel.remove();
            /* Reset Buy Now button so it's not stuck on Processing… */
            var orderBtn = document.getElementById('pus-order-btn');
            if (orderBtn && state.selectedVariant) {
              orderBtn.disabled    = false;
              orderBtn.textContent = 'Buy Now — ' + formatMoney(state.selectedVariant.price);
            }
          }
        });
      });
  }

  // ─── Cart Count Refresh ───────────────────────────────────────────────────────
  async function refreshCartCount() {
    try {
      const cart = await fetch('/cart.js').then(r => r.json());
      document.querySelectorAll('.cart-count-bubble span').forEach(el => {
        el.textContent = cart.item_count;
      });
    } catch (_) {}
  }

  // ─── Money Formatting ─────────────────────────────────────────────────────────
  function formatMoney(cents) {
    const amount = (cents / 100).toFixed(2);
    return moneyFormat
      .replace('{{amount}}', amount)
      .replace('{{amount_no_decimals}}', Math.round(cents / 100))
      .replace('{{amount_with_comma_separator}}', amount.replace('.', ','));
  }

  // ─── Progress Animation ───────────────────────────────────────────────────────
  const LOADING_MESSAGES = {
    'the-signature-portrait': [
      { pct: 0,  text: 'Preparing the canvas…' },
      { pct: 30, text: 'Painting your portrait…' },
      { pct: 65, text: 'Adding the final brushstrokes…' },
    ],
    'a-spot-by-the-sea': [
      { pct: 0,  text: 'Taking them to the shore…' },
      { pct: 30, text: 'Painting the light on the water…' },
      { pct: 65, text: 'Adding the final brushstrokes…' },
    ],
    'mountain-majesty': [
      { pct: 0,  text: 'Heading to the peaks…' },
      { pct: 30, text: 'Capturing the alpine light…' },
      { pct: 65, text: 'Adding the final brushstrokes…' },
    ],
    'garden-portrait': [
      { pct: 0,  text: 'Stepping into the garden…' },
      { pct: 30, text: 'Letting the light filter through…' },
      { pct: 65, text: 'Adding the final brushstrokes…' },
    ],
    'royal-portrait': [
      { pct: 0,  text: 'Preparing the royal canvas…' },
      { pct: 30, text: 'Draping the velvet backdrop…' },
      { pct: 65, text: 'Adding the final brushstrokes…' },
    ],
    default: [
      { pct: 0,  text: 'Creating your portrait…' },
      { pct: 30, text: 'Painting the details…' },
      { pct: 65, text: 'Adding finishing touches…' },
    ],
  };

  let progressTimer = null;
  let progressValue = 0;

  function updateProgressUI(pct, text, slowFinal) {
    const fill   = document.getElementById('pus-progress-fill');
    const pctEl  = document.getElementById('pus-loading-pct');
    const textEl = document.getElementById('pus-loading-text');
    if (fill) {
      fill.style.transition = slowFinal ? 'width 2s ease-out' : 'width 0.5s ease';
      fill.style.width = pct + '%';
    }
    if (pctEl)  pctEl.textContent = Math.round(pct) + '%';
    if (textEl && text) textEl.textContent = text;
  }

  function startProgressAnimation() {
    progressValue = 0;
    const messages = LOADING_MESSAGES[state.productHandle] || LOADING_MESSAGES.default;
    updateProgressUI(0, messages[0].text);

    progressTimer = setInterval(() => {
      // Ease toward 88%: faster start, slows near ceiling
      const remaining = 88 - progressValue;
      const increment = Math.max(0.3, remaining * 0.038);
      progressValue = Math.min(88, progressValue + increment);

      // Pick the most recent message whose threshold we've passed
      const msg = [...messages].reverse().find(m => progressValue >= m.pct);
      updateProgressUI(progressValue, msg?.text);
    }, 300);
  }

  function stopProgressAnimation(onComplete) {
    clearInterval(progressTimer);
    progressTimer = null;

    const startPct  = progressValue;
    const startTime = performance.now();
    const duration  = 1800; // ms to travel from current % to 100%

    function animateFinal(now) {
      const elapsed = now - startTime;
      const t       = Math.min(1, elapsed / duration);
      // Ease-out cubic — decelerates as it approaches 100%
      const eased   = 1 - Math.pow(1 - t, 3);
      const pct     = startPct + (100 - startPct) * eased;

      const fill  = document.getElementById('pus-progress-fill');
      const pctEl = document.getElementById('pus-loading-pct');
      const textEl = document.getElementById('pus-loading-text');
      if (fill)  { fill.style.transition = 'none'; fill.style.width = pct + '%'; }
      if (pctEl)   pctEl.textContent = Math.round(pct) + '%';
      if (textEl && t > 0.4) textEl.textContent = 'Almost ready…';

      if (t < 1) {
        requestAnimationFrame(animateFinal);
      } else {
        if (onComplete) onComplete();
      }
    }

    requestAnimationFrame(animateFinal);
  }

  // ─── Step Visibility ─────────────────────────────────────────────────────────
  function showStep(step) {
    ['upload', 'generating', 'preview', 'confirmed'].forEach((s) => {
      document.getElementById(`pus-${s}`)?.classList.toggle('pus-hidden', s !== step);
    });
  }

  // ─── Error ───────────────────────────────────────────────────────────────────
  function showError(message) {
    const el = document.getElementById('pus-error');
    if (el) { el.textContent = message; el.classList.add('pus-visible'); }
  }

  function clearError() {
    document.getElementById('pus-error')?.classList.remove('pus-visible');
  }

  // ─── Boot ─────────────────────────────────────────────────────────────────────
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();